import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Palette, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "An error occurred. Please check your connection and try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      if (res.data.token) {
        setResetLink(`${window.location.origin}/reset-password?token=${res.data.token}`);
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      if (err.response?.data?.detail) {
        setError(formatApiErrorDetail(err.response.data.detail));
      } else if (err.message === 'Network Error') {
        setError("Unable to connect to server. Please check your connection and try again.");
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Request failed: " + String(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 md:p-12 bg-[#FAFAFA]">
        <Card className="w-full max-w-md border-slate-200 shadow-sm rounded-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-xl bg-blue-600">
                <Palette className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {sent ? 'Check your email' : 'Reset your password'}
            </CardTitle>
            <CardDescription className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>
              {sent 
                ? 'If an account exists with that email, we\'ve sent a password reset link.'
                : 'Enter your email and we\'ll send you a reset link.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <CheckCircle2 className="h-16 w-16 text-emerald-500" />
                </div>
                {resetLink ? (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600 text-center" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      Click the link below to reset your password. It expires in 1 hour.
                    </p>
                    <Link to={resetLink.replace(window.location.origin, '')} data-testid="reset-link">
                      <Button className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reset-password-link-button">
                        Reset My Password
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600 text-center" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    If an account exists with that email, a reset link has been generated.
                  </p>
                )}
                <div className="flex flex-col gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setSent(false); setEmail(''); setResetLink(''); }}
                    className="w-full rounded-lg"
                    data-testid="try-another-email"
                  >
                    Try another email
                  </Button>
                  <Link to="/login" className="w-full">
                    <Button className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="back-to-login-button">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Sign in
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="forgot-password-error">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="teacher@school.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email-input"
                    className="bg-slate-50 border-slate-200 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg py-2"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>

                <div className="text-center">
                  <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="back-to-login-link">
                    <ArrowLeft className="h-3 w-3 inline mr-1" />
                    Back to Sign in
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      <div
        className="hidden lg:block bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/1821377/pexels-photo-1821377.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/40 to-amber-500/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Create(ED)
          </h2>
          <p className="text-lg opacity-90" style={{ fontFamily: 'Figtree, sans-serif' }}>
            The art teacher workspace for Preschool through High School.
          </p>
        </div>
      </div>
    </div>
  );
}
